import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import Typography from "components/typography";
import Dropdown from "components/inputs/dropdown";
import PrimaryButton from "components/buttons/primary-button";
import DateInput from "components/inputs/date-input";
import { IFilter } from "../..";

interface IProps {
  close: () => void;
  setFilter(value: IFilter): void;
  filter: IFilter;
}
const Filter = ({ close, setFilter, filter }: IProps) => {
  const filterData = filter.filter;
  const [dates, setDates] = useState<{ from: null | Date; to: null | Date }>({
    from: filterData[0].filter.from,
    to: filterData[0].filter.to
  });
  const [artist, setArtist] = useState(filterData[1].filter);
  const [status, setStatus] = useState(filterData[2].filter);
  const [type, setType] = useState(filterData[3].filter);

  const ApplyFilters = () => {
    setFilter({
      filter: [
        { section: "Date", filter: { to: dates.to, from: dates.from } },
        { section: "Artist", filter: artist },
        { section: "Status", filter: status },
        { section: "Type", filter: type }
      ]
    });
    close();
  };

  useEffect(() => {
    setDates({ from: filterData[0].filter.from, to: filterData[0].filter.to });
    setArtist(filterData[1].filter);
    setStatus(filterData[2].filter);
    setType(filterData[3].filter);
  }, [filterData]);

  const ClearDates = () => {
    setFilter({
      filter: [
        {
          section: "Date",
          filter: { to: null, from: null }
        },
        filter.filter[1],
        filter.filter[2],
        filter.filter[3]
      ]
    });
  };

  const ClearArtists = () => {
    setFilter({
      filter: [
        filter.filter[0],
        { section: "Artist", filter: "" },
        filter.filter[2],
        filter.filter[3]
      ]
    });
  };

  const ClearStatus = () => {
    setFilter({
      filter: [
        filter.filter[0],
        filter.filter[1],
        { section: "Status", filter: "" },
        filter.filter[3]
      ]
    });
  };

  const ClearType = () => {
    setFilter({
      filter: [
        filter.filter[0],
        filter.filter[1],
        filter.filter[2],
        { section: "Type", filter: "" }
      ]
    });
  };

  const Reset = () => {
    setFilter({
      filter: [
        { section: "Date", filter: { to: null, from: null } },
        { section: "Artist", filter: "" },
        { section: "Status", filter: "" },
        { section: "Type", filter: "" }
      ]
    });
  };
  return (
    <Container>
      <Header>
        <Typography weight="semibold" color={Colors.white300}>
          Filter
        </Typography>
        <CancelIcon onClick={close}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M15.8327 4.1665L4.16602 15.8332M4.16602 4.1665L15.8327 15.8332"
              stroke="#DDDDDD"
              stroke-width="1.25"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </CancelIcon>
      </Header>
      <Body>
        <Section>
          <SectionHeader>
            <Typography color={Colors.grey500}>Date Listed</Typography>
            <ClearDiv onClick={ClearDates}>
              <Typography color={Colors.yellow400}>Clear</Typography>
            </ClearDiv>
          </SectionHeader>
          <DateContainer>
            <DateInput
              label="From:"
              placeholder="Pick date"
              selectDate={(date) => {
                setDates({ ...dates, from: date });
              }}
              initialDate={dates.from}
            />
            <DateInput
              label="To:"
              placeholder="Pick date"
              selectDate={(date) => {
                setDates({ ...dates, to: date });
              }}
              initialDate={dates.to}
              minDate={dates.from}
            />
          </DateContainer>
        </Section>
        <Section>
          <SectionHeader>
            <Typography color={Colors.grey500}>Artist</Typography>
            <ClearDiv onClick={ClearArtists}>
              <Typography color={Colors.yellow400}>Clear</Typography>
            </ClearDiv>
          </SectionHeader>
          <Dropdown
            items={["Vectra", "Dave", "Stormzy"]}
            selectItem={(value) => setArtist(value)}
            chosenItem={artist}
          />
        </Section>
        <Section>
          <SectionHeader>
            <Typography color={Colors.grey500}>Status</Typography>
            <ClearDiv onClick={ClearStatus}>
              <Typography color={Colors.yellow400}>Clear</Typography>
            </ClearDiv>
          </SectionHeader>
          <Dropdown
            items={["Released", "Processing", "Inactive"]}
            selectItem={(value) => setStatus(value)}
            chosenItem={status}
          />
        </Section>
        <Section>
          <SectionHeader>
            <Typography color={Colors.grey500}>Release Type</Typography>
            <ClearDiv onClick={ClearType}>
              <Typography color={Colors.yellow400}>Clear</Typography>
            </ClearDiv>
          </SectionHeader>
          <Dropdown
            items={["Album", "Single"]}
            selectItem={(value) => setType(value)}
            chosenItem={type}
          />
        </Section>
      </Body>
      <Footer>
        <Button>
          <PrimaryButton
            onClick={Reset}
            text="Reset All Filters"
            variant="p-fade"
          />
        </Button>
        <Button>
          <PrimaryButton onClick={ApplyFilters} text="Apply Filters" />
        </Button>
      </Footer>
    </Container>
  );
};

export default Filter;

const Container = styled.div`
  width: 44.8rem;
  height: fit-content;
  border-radius: 0.8rem;
  background-color: ${Colors.grey900};
`;

const Header = styled.div`
  padding: 1.6rem;
  background-color: ${Colors.grey700};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-right-radius: 0.8rem;
  border-top-left-radius: 0.8rem;
  border-bottom: 0.5px solid ${Colors.grey600};
`;

const CancelIcon = styled.div`
  width: 2rem;
  height: 2rem;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 2.4rem 1.6rem;
  padding-bottom: 0;
`;

const Section = styled.div`
  padding-bottom: 2.4rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.8rem;
`;
const ClearDiv = styled.div`
  cursor: pointer;
`;

const DateContainer = styled.div`
  display: grid;
  grid-template-columns: 45% 45%;
  justify-content: space-between;
`;
const Footer = styled.div`
  width: 100%;
  padding: 2rem 1.6rem;
  border-top: 1px solid ${Colors.grey600};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Button = styled.div`
  width: fit-content;
`;
